import { METRICS, METRICS_MAP } from 'features/command-center/constants';
import { capitalizeName } from 'features/command-center/utils';
import { resolveCssVars } from 'UI/theme/cssVariables';
import { ThemeModes } from 'UI/theme/themeMode';

import {
  DARK_HEATMAP_TEXT_COLOR,
  DARK_HEATMAP_TONES,
  MAP_METRIC_MEDIUM_COLORS
} from './TeamMembersHealth.constants';

/**
 * Adds a heatmap color to every metric. Theme colors are CSS variables, so they are resolved to
 * concrete hex values first; without `cssVars` they fall back to the light palette. In dark mode
 * cells use the palette's tonal steps and get a `textColor` that keeps WCAG AA contrast.
 * @param {Object} response API response
 * @param {Object<string, string>} [cssVars] active theme variables from `useResolvedTheme()`
 * @param {'light'|'dark'} [mode='light'] resolved theme mode from `useResolvedTheme()`
 */
export const formatTeamMembersHealthResponse = (
  response,
  cssVars = {},
  mode = ThemeModes.Light
) => {
  const isDark = mode === ThemeModes.Dark;
  const baseColors = resolveCssVars(MAP_METRIC_MEDIUM_COLORS, cssVars);
  const darkTones = isDark && resolveCssVars(DARK_HEATMAP_TONES, cssVars);
  const darkTextColor = isDark && resolveCssVars(DARK_HEATMAP_TEXT_COLOR, cssVars);
  const initialMinMaxValues = {
    atRisk: { min: Number.MAX_VALUE, max: Number.MIN_VALUE },
    behind: { min: Number.MAX_VALUE, max: Number.MIN_VALUE },
    onTrack: { min: Number.MAX_VALUE, max: Number.MIN_VALUE },
    inTraining: { min: Number.MAX_VALUE, max: Number.MIN_VALUE },
    under3Months: { min: Number.MAX_VALUE, max: Number.MIN_VALUE }
  };

  const getUpdatedMinMax = (current, total) => ({
    min: Math.min(current.min, total),
    max: Math.max(current.max, total)
  });

  const minMaxValuesResult = response.data.reduce((acc, item) => {
    const { atRisk, behind, onTrack, inTraining, under3Months } = item;

    return {
      atRisk: getUpdatedMinMax(acc.atRisk, atRisk.total),
      behind: getUpdatedMinMax(acc.behind, behind.total),
      onTrack: getUpdatedMinMax(acc.onTrack, onTrack.total),
      inTraining: getUpdatedMinMax(acc.inTraining, inTraining.total),
      under3Months: getUpdatedMinMax(acc.under3Months, under3Months.total)
    };
  }, initialMinMaxValues);

  const buildCell = (metric, key) => {
    const { min, max } = minMaxValuesResult[key];
    if (!darkTones) {
      return { ...metric, color: createColorVariants(baseColors[key], metric.total, min, max) };
    }
    const color = darkTones[key][getHeatmapVariant(metric.total, min, max)];
    return { ...metric, color, textColor: darkTextColor };
  };

  const formattedData = response.data.map(item => ({
    ...item,
    minMaxValues: minMaxValuesResult,
    teamName: {
      id: item.managerEmpNo,
      managerName: capitalizeName(item.managerName)
    },
    atRisk: buildCell(item.atRisk, METRICS_MAP.AtRisk),
    behind: buildCell(item.behind, METRICS_MAP.Behind),
    onTrack: buildCell(item.onTrack, METRICS_MAP.OnTrack),
    inTraining: buildCell(item.inTraining, METRICS_MAP.InTraining),
    under3Months: buildCell(item.under3Months, METRICS_MAP.Under3Months)
  }));

  return {
    ...response,
    data: formattedData
  };
};

export const teamMembersHealthParamsExtender = () => ({
  groupBy: 'team',
  metrics: Object.values(METRICS)
    .map(({ paramName }) => paramName)
    .join(',')
});

const calculateHue = (max, r, g, b, d) => {
  const hueMap = {
    [r]: (g - b) / d + (g < b ? 6 : 0),
    [g]: (b - r) / d + 2,
    [b]: (r - g) / d + 4,
    default: 0
  };

  return ((hueMap[max] || hueMap.default) / 6) * 360;
};

const hexToHsl = hex => {
  const hexCleaned = hex.replace(/^#/, '');

  const hexFull = hexCleaned.length === 3 ? [...hexCleaned].map(x => x + x).join('') : hexCleaned;

  const r = Number.parseInt(hexFull.substring(0, 2), 16) / 255;
  const g = Number.parseInt(hexFull.substring(2, 4), 16) / 255;
  const b = Number.parseInt(hexFull.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return {
      h: 0,
      s: 0,
      l: l * 100
    };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  return {
    h: calculateHue(max, r, g, b, d),
    s: s * 100,
    l: l * 100
  };
};

const hslToHex = (hue, saturation, luminance) => {
  const adjustedS = saturation / 100;
  const adjustedL = luminance / 100;

  const k = n => (n + hue / 30) % 12;
  const a = adjustedS * Math.min(adjustedL, 1 - adjustedL);
  const f = n => adjustedL - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

  const toHex = x => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };

  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
};

/**
 * Splits the value range in thirds: the lowest third is `light`, the middle `medium`, the top `dark`.
 */
const getHeatmapVariant = (value, minValue, maxValue) => {
  const step = (maxValue - minValue) / 3;
  if (value <= minValue + step) return 'light';
  if (value <= minValue + 2 * step) return 'medium';
  return 'dark';
};

export const createColorVariants = (color, value, minValue, maxValue) => {
  const { h, s, l } = hexToHsl(color);

  const variant = getHeatmapVariant(value, minValue, maxValue);

  const getAdjustedLightness = (variantBase, lightness) => {
    const lightnessMap = {
      dark: lightness,
      medium: Math.min(l + (100 - lightness) * 0.5, 100),
      light: Math.min(l + (100 - lightness) * 0.75, 100),
      default: lightness
    };

    return lightnessMap[variantBase] || lightnessMap.default;
  };

  const adjustedLightness = getAdjustedLightness(variant, l);

  return hslToHex(h, s, adjustedLightness);
};
